/**
 * Add to Cart Handler
 * Direct conversion from TypeScript master add-to-cart.directive.ts
 */
// @ts-nocheck
import CartManager from '@theme/cart-manager';
import { SideCartManager } from '@theme/side-cart-manager';
import { CartUpdateEvent } from '@theme/events';
import {
  countLimitedSwatchUnits,
  isLimitedSwatchProductType,
  notifyDuplicateSwatch,
  notifySwatchLimit,
  SWATCH_LIMIT
} from '@theme/swatch-limit';

const MIRROR_PRICING_KEYS = [
  '_mirrorShape',
  '_mirrorWidthIn',
  '_mirrorHeightIn',
  '_mirrorColorTier',
  '_mirrorFrameTier',
  '_mirrorConfig',
];

/**
 * @param {Record<string, string>} props
 * @returns {{ valid: boolean, missing: string[] }}
 */
function validateMirrorPricingProps(props) {
  const missing = [];
  if (!props || !props._mirrorShape) missing.push('_mirrorShape');
  if (!props._mirrorWidthIn) missing.push('_mirrorWidthIn');
  if (String(props._mirrorShape || '').toLowerCase() !== 'round' && !props._mirrorHeightIn) {
    missing.push('_mirrorHeightIn');
  }
  if (!props._mirrorColorTier) missing.push('_mirrorColorTier');
  if (!props._mirrorFrameTier) missing.push('_mirrorFrameTier');
  if (!props._mirrorConfig) missing.push('_mirrorConfig');
  return { valid: missing.length === 0, missing };
}

const FINAL_SALE_PROPERTY_KEY = 'Note';
const FINAL_SALE_PROPERTY_VALUE = 'Final Sale';
const FINAL_SALE_LEGACY_KEY = 'Final Sale';

/**
 * shipping rate line item - variant metafield
 */
const SHIPPING_RATE_PROPERTY_KEY = '_Shipping Rate';

/**
 * Template setting may hide variant Final Sale tag and line-item Note from metafield.
 * @returns {boolean}
 */
function isVariantFinalSaleTagHiddenByTemplate() {
  return !!document.querySelector('.product-metas__price[data-hide-variant-final-sale-tag="true"]');
}

/**
 * Normalize Liquid/JSON metafield value to a plain string.
 * @param {unknown} raw
 * @returns {string}
 */
function metafieldValueToString(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object') {
    const nested = raw.value ?? raw;
    if (nested == null || nested === '') return '';
    if (typeof nested === 'object') return '';
    return String(nested).trim();
  }
  return String(raw).trim();
}

/**
 * Reads variant metafield custom.final__sale from PDP JSON.
 * @param {number|string} variantId
 * @returns {boolean}
 */
function isVariantFinalSaleFromJson(variantId) {
  const scriptEl = document.getElementById('product-options-variant-data');
  if (!scriptEl || !variantId) return false;

  try {
    const data = JSON.parse(scriptEl.textContent);
    const index = (data.variants || []).findIndex((v) => String(v.id) === String(variantId));
    if (index === -1) return false;

    const metafields = data.variants_metafields?.[index] || {};
    const raw = metafields.final__sale ?? metafields.custom?.final__sale;
    if (raw === true || raw === 'true') return true;
    if (raw && typeof raw === 'object') {
      return raw.value === true || raw.value === 'true';
    }
  } catch (error) {
    console.error('[ATC] Failed to read final__sale metafield:', error);
  }

  return false;
}

/**
 * shipping rate line item
 * @param {number|string} variantId
 * @returns {string}
 */
function getShippingRateFromJson(variantId) {
  const scriptEl = document.getElementById('product-options-variant-data');
  if (!scriptEl || !variantId) return '';

  try {
    const data = JSON.parse(scriptEl.textContent);
    const index = (data.variants || []).findIndex((v) => String(v.id) === String(variantId));
    if (index === -1) return '';

    const metafields = data.variants_metafields?.[index] || {};
    const fromVariant = metafieldValueToString(metafields.my_fields?.shipping_rate);
    if (fromVariant) return fromVariant;

    return metafieldValueToString(data.product_metafields?.new_my_fields?.shipping_rate);
  } catch (error) {
    console.error('[ATC] Failed to read shipping_rate metafield:', error);
  }

  return '';
}

/**
 * Sets hidden line-item property _Shipping Rate from variant/product metafield.
 * Underscore key is excluded from cart/checkout UI; available on order line items.
 * @param {Record<string, string>} props
 * @param {number|string} variantId
 * @returns {Record<string, string>}
 */
function applyShippingRateProperty(props, variantId) {
  const nextProps = props || {};
  const rate = getShippingRateFromJson(variantId);

  if (rate) {
    nextProps[SHIPPING_RATE_PROPERTY_KEY] = rate;
  } else {
    delete nextProps[SHIPPING_RATE_PROPERTY_KEY];
  }

  return nextProps;
}

/**
 * Sets visible line-item property Note: Final Sale for checkout/order notes.
 * Does not wipe other Note values (e.g. product-level final_sale not_text).
 * @param {Record<string, string>} props
 * @param {number|string} variantId
 * @returns {Record<string, string>}
 */
function applyFinalSaleProperty(props, variantId) {
  const nextProps = props || {};

  if (isVariantFinalSaleTagHiddenByTemplate()) {
    delete nextProps[FINAL_SALE_LEGACY_KEY];
    const noteValHidden = String(nextProps[FINAL_SALE_PROPERTY_KEY] || '').trim();
    if (noteValHidden === FINAL_SALE_PROPERTY_VALUE) {
      delete nextProps[FINAL_SALE_PROPERTY_KEY];
    }
    return nextProps;
  }

  const fromMetafield = isVariantFinalSaleFromJson(variantId);

  // Migrate legacy key → Note
  if (nextProps[FINAL_SALE_LEGACY_KEY] != null) {
    const legacyVal = String(nextProps[FINAL_SALE_LEGACY_KEY]).trim();
    if (!String(nextProps[FINAL_SALE_PROPERTY_KEY] || '').trim() && legacyVal) {
      nextProps[FINAL_SALE_PROPERTY_KEY] = legacyVal === 'true' ? FINAL_SALE_PROPERTY_VALUE : legacyVal;
    }
    delete nextProps[FINAL_SALE_LEGACY_KEY];
  }

  const noteVal = String(nextProps[FINAL_SALE_PROPERTY_KEY] || '').trim();
  const fromFormExact = noteVal === FINAL_SALE_PROPERTY_VALUE;

  if (fromMetafield || fromFormExact) {
    // Keep a longer existing Note (e.g. "Final Sale - Non Returnable Item")
    if (!noteVal || noteVal === FINAL_SALE_PROPERTY_VALUE) {
      nextProps[FINAL_SALE_PROPERTY_KEY] = FINAL_SALE_PROPERTY_VALUE;
    }
  } else if (noteVal === FINAL_SALE_PROPERTY_VALUE) {
    // Only remove our exact Final Sale value, not other Notes
    delete nextProps[FINAL_SALE_PROPERTY_KEY];
  }

  return nextProps;
}

class AddToCartHandler {
  constructor(button) {
    this.button = button;
    this.addingText = button.getAttribute('adding-text') || 'Adding...';
    this.noProps = button.hasAttribute('add-to-cart-no-props');
    this.isSwatch = button.getAttribute('add-to-cart-swatch') === 'true';
    this.productType = button.getAttribute('data-product-type') || '';
    this.rugFlowHandle = button.getAttribute('custom-rug-flow-handle');
    this.flow = button.getAttribute('add-to-cart-flow') || 'default';
    this.defaultText = button.textContent;
    this.productForm = button.closest('form');
    this.previousText = null;
    this.cartManager = CartManager.getInstance();
    this.sideCartManager = SideCartManager.getInstance();
    this.queuedOperation = new Map();
    this.isBound = false;
    this.bindEvents();
  }
  getCurrentVariantId() {
    // Priority 1: Button attribute (matches TypeScript @HostAttr behavior)
    const attrId = this.button.getAttribute('add-to-cart');
    if (attrId) {
      return Number(attrId);
    }
    
    // Priority 2: Form input (fallback)
    if (this.productForm) {
      const idInput = this.productForm.querySelector('[name="id"]');
      if (idInput && idInput.value) {
        return Number(idInput.value);
      }
    }
    
    return null;
  }
  bindEvents() {
    // Direct conversion from TypeScript line 53-58
    if (this.isBound) return;
    this.button.addEventListener('click', this.onClick.bind(this));
    if (this.productForm) {
      this.productForm.addEventListener('submit', this.preventFormSubmit.bind(this));
    }
    this.isBound = true;
  }

  preventFormSubmit(e) {
    // Direct conversion from TypeScript line 227
    e.preventDefault();
  }

  async onClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const id = this.getCurrentVariantId();
    if (!id) return;
    const requestedQuantity = this.isSwatch
      ? 1
      : Math.max(1, Number(this.productForm?.querySelector('[name="quantity"]')?.value || 1));
    const isLimitedSwatch = isLimitedSwatchProductType(this.productType);
    if (this.isSwatch || isLimitedSwatch) {
      const isAllowed = this.onSwatchAddCallback(id, requestedQuantity, isLimitedSwatch);
      if (!isAllowed) {
        return;
      }
    }

    // Direct conversion from TypeScript line 73-76
    if (window.gsap) {
      window.gsap.set(this.button, { width: this.button.offsetWidth });
    } else {
      this.button.style.width = `${this.button.offsetWidth}px`;
    }
    this.previousText = this.button.textContent;
    this.button.textContent = this.addingText;
    this.button.disabled = true;

    const formData = new FormData(this.productForm || document.createElement('form'));

    // Extract properties (matches TypeScript line 80-116)
    const prodProps = {};
    if (this.productForm && !this.noProps) {
      const quantityInput = this.productForm.querySelector('[name="quantity"]');
      const giftCardRecipientForm = this.productForm.querySelector('recipient-form');

      if (giftCardRecipientForm && !this.productForm.checkValidity()) {
        this.productForm.reportValidity();
        this.resetButton();
        return;
      }

      if (quantityInput && !quantityInput.checkValidity()) {
        quantityInput.reportValidity();
        this.resetButton();
        return;
      }

      const rawQty = formData.get('quantity');
      const quantity = Math.max(1, Number(rawQty || 1));

      if (!this.noProps) {
        Array.from(formData.entries())
          .filter(([key]) => key.startsWith('properties['))
          .forEach(([key, value]) => {
            prodProps[key] = `${value}`;
          });

        // Convert bracket notation to dot notation (matches TypeScript line 107-116)
        Object.keys(prodProps).forEach(key => {
          let path = key.replace(/\[/g, '.').replace(/\]/g, '').split('.');
          let last = path.pop();
          prodProps[last] = prodProps[key];
          delete prodProps[key];
        });
      }
    }

    // Handle product add-ons (matches TypeScript line 119-136)
    const productAddOnElement = this.productForm?.querySelector('[product-add-on]');
    const productAddOnChecked = productAddOnElement?.querySelector('[product-add-on-toggle]:checked');

    if (productAddOnElement && productAddOnChecked) {
      const productAddOnVariantId = productAddOnElement.getAttribute('product-add-on');
      const productAddOnTitle = productAddOnElement.getAttribute('product-add-on-title');
      const productAddOnPrice = productAddOnElement.getAttribute('product-add-on-price');
      const productAddOnMatchQuantity = productAddOnElement.getAttribute('product-add-on-match-quantity');

      if (productAddOnChecked && productAddOnTitle && productAddOnVariantId && productAddOnPrice) {
        Object.assign(prodProps, {
          _add_on_id: productAddOnVariantId,
          _add_on_title: productAddOnTitle,
          _add_on_price: productAddOnPrice,
          _add_on_match_qty: productAddOnMatchQuantity
        });
      }
    }

    // Sync Final Sale line property from variant metafield (shows on checkout / order notes)
    applyFinalSaleProperty(prodProps, id);
    // Sync Shipping Rate (hidden from cart/checkout via _; visible on order line items)
    applyShippingRateProperty(prodProps, id);

    // Handle custom rugs flow (matches TypeScript line 138-168)
    if (this.flow === 'custom-rugs' && this.rugFlowHandle) {
      try {
        const customRugsJSON = document.getElementById('CustomRugsJSON');
        if (!customRugsJSON) {
          console.error('CustomRugsJSON not found');
          this.resetButton();
          return;
        }

        const flows = JSON.parse(customRugsJSON.textContent);
        const data = flows.find(flow => flow.handle === this.rugFlowHandle);

        if (!data) {
          console.error('Flow data not found for handle:', this.rugFlowHandle);
          this.resetButton();
          return;
        }

        const products = [data.serging_product, data.rug_pad_product];
        const variants = products.flatMap(p => p.variants || []);

        // Check if rug pad is selected (might be "No Rug Pad" or empty)
        const rugPadId = prodProps['rug pad'] ? +prodProps['rug pad'] : null;
        const finishingId = prodProps['finishing'] ? +prodProps['finishing'] : null;

        const rugPad = rugPadId ? variants.find(v => v.id === rugPadId) : null;
        const finishingProduct = finishingId ? products.find(p => (p.variants || []).some(v => v.id === finishingId)) : null;
        const finishingVariant = finishingId && finishingProduct ? finishingProduct.variants?.find(v => v.id === finishingId) : null;

        // Finishing is required, rug pad is optional
        if (!finishingVariant) {
          console.error('Finishing variant not found or not selected');
          this.resetButton();
          return;
        }

        // Direct conversion from TypeScript line 149-150
        // Get directive instance (in JS, we'll use window.customRugsHandler)
        const directive = window.customRugsHandler || null;
        const chosenArea = directive ? directive.chosenArea : 1;
        const chosenPerimeter = directive ? directive.chosenPerimeter : 1;
        const currentPrice = directive ? directive.currentPrice : 0;

        const _rugBundleId = `${Date.now()}`;
        const properties = this.formatCustomRugProperties(prodProps, data);
        properties['_rugBundleId'] = _rugBundleId;
        if (prodProps[FINAL_SALE_PROPERTY_KEY]) {
          properties[FINAL_SALE_PROPERTY_KEY] = prodProps[FINAL_SALE_PROPERTY_KEY];
        }
        if (prodProps[SHIPPING_RATE_PROPERTY_KEY]) {
          properties[SHIPPING_RATE_PROPERTY_KEY] = prodProps[SHIPPING_RATE_PROPERTY_KEY];
        }

        // Direct conversion from TypeScript line 154-156
        const rugQuantity = Math.ceil(chosenArea);
        const rugPadQuantity = Math.ceil(chosenArea);
        const finishingQuantity = Math.ceil(chosenPerimeter);

        // Direct conversion from TypeScript line 158-162
        const items = [
          { id, quantity: rugQuantity, properties: { ...properties, _rugBundlePrice: currentPrice } },
        ];

        // Add rug pad and finishing if they exist
        if (rugPad && rugPad.id) {
          items.push({ id: rugPad.id, quantity: rugPadQuantity, properties: { _rugBundleId } });
        }
        if (finishingVariant && finishingVariant.id) {
          items.push({ id: finishingVariant.id, quantity: finishingQuantity, properties: { _rugBundleId } });
        }

        // Direct conversion from TypeScript line 164-165
        this.queuedOperation = this.queuedOperation || new Map();
        this.queuedOperation.set(_rugBundleId, items);
        this.addMany(items)
          .then(() => this.onAddCallback())
          .catch((error) => this.onAddRejected(error));
      } catch (error) {
        console.error('Error handling custom rugs flow:', error);
        this.resetButton();
      }
    } else if (
      this.flow === 'custom-mirror' ||
      (this.productForm && this.productForm.querySelector('[data-mirror-calculator]'))
    ) {
      // JS-assembled props (most complete source); FormData hidden inputs are the fallback.
      const mirrorProps =
        typeof window.getMirrorCartLineProperties === 'function'
          ? window.getMirrorCartLineProperties()
          : null;
      // JS overrides FormData; if JS returns null, FormData hidden inputs carry the values.
      const mergedProps = mirrorProps ? { ...prodProps, ...mirrorProps } : { ...prodProps };
      applyFinalSaleProperty(mergedProps, id);
      applyShippingRateProperty(mergedProps, id);
      const mirrorValidation = validateMirrorPricingProps(mergedProps);
      if (!mirrorValidation.valid) {
        console.error(
          '[Mirror ATC] Mirror configuration incomplete — missing:',
          mirrorValidation.missing.join(', '),
          mergedProps,
        );
        this.resetButton();
        return;
      }
      // Mirror lines always add one configured unit; ignore stray quantity inputs.
      const qty = 1;
      if (typeof console !== 'undefined' && console.info) {
        console.info('[Mirror ATC] add payload', {
          id,
          qty,
          pricing: MIRROR_PRICING_KEYS.reduce(function (acc, key) {
            acc[key] = mergedProps[key];
            return acc;
          }, {}),
        });
      }
      this.cartManager
        .add(id, qty, mergedProps)
        .then(this.onAddCallback.bind(this), this.onAddRejected.bind(this));
    } else {
      // Direct conversion from TypeScript line 167
      const rawQty = formData.get('quantity');
      const qty = this.isSwatch ? 1 : Math.max(1, Number(rawQty || 1));
      this.cartManager.add(id, qty, prodProps).then(this.onAddCallback.bind(this), this.onAddRejected.bind(this));
    }
  }

  async add(id, quantity = 1, properties = {}) {
    return this.cartManager.add(id, quantity, properties);
  }

  async addMany(items) {
    return this.cartManager.addMany(items, false);
  }

  async onAddCallback(data) {
    this.resetButton();
    const cart = await this.cartManager.get();
    document.dispatchEvent(new CartUpdateEvent({ cart: cart }));
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: cart },
      bubbles: true
    }));
    this.sideCartManager.open();
  }

  onAddRejected(data) {
    // Direct conversion from TypeScript line 179-214
    console.log(data, "data");
    if (this.queuedOperation && this.queuedOperation.size > 0) {
      this.cartManager.get().then(cart => {
        const uniqueBundleIds = [...new Set(cart.items.map(i => i.properties?._rugBundleId).filter(Boolean))];

        uniqueBundleIds?.forEach(id => {
          const queuedBundles = this.queuedOperation.get(id);
          const queuedBundleItems = queuedBundles ? Object.values(queuedBundles) : [];
          const bundleItems = cart.items.filter(item => item.properties?._rugBundleId === id);

          if (!queuedBundleItems || !bundleItems) return;

          bundleItems.forEach(item => {
            const queuedBundleItemMatch = queuedBundleItems.find(obj => obj.id === item.id);

            if (queuedBundleItemMatch?.quantity !== item.quantity) {
              const itemChanges = [];

              bundleItems.forEach((i) => {
                itemChanges.push({ id: i.key, quantity: 0 });
              });

              this.cartManager.changeMany(itemChanges);
              this.queuedOperation.delete(id);
              return;
            }
          });
        });
      });
    }

    this.resetButton();
  }

  resetButton() {
    if (this.previousText) {
      this.button.textContent = this.previousText;
      this.previousText = null;
    } else {
      this.button.textContent = this.defaultText;
    }
    this.button.disabled = false;
    this.button.removeAttribute('style');
  }

  formatCustomRugProperties(props, flowData) {
    // Matches TypeScript formatCustomRugProperties line 228-269
    const ret = {};

    if (['round', 'octagon'].includes(props['shape'])) {
      const diameterInch = +props['diameter-inches'] || 0;
      const diameterFeet = +props['diameter-feet'] || 0;
      ret['Dimension'] = `${diameterFeet}'${diameterInch > 0 ? `${diameterInch}"` : ''}`;
    } else if (props['shape'] === 'square') {
      const widthInch = +props['width-inches'] || 0;
      const widthFeet = +props['width-feet'] || 0;
      const dimension = `${widthFeet}'${widthInch > 0 ? `${widthInch}"` : ''}`;
      ret['Dimension'] = `${dimension} x ${dimension}`;
    } else if (props['shape'] === 'rectangle') {
      const widthInch = +props['width-inches'] || 0;
      const widthFeet = +props['width-feet'] || 0;
      const lengthInch = +props['length-inches'] || 0;
      const lengthFeet = +props['length-feet'] || 0;
      ret['Dimension'] = `${widthFeet}'${widthInch > 0 ? `${widthInch}"` : ''} x ${lengthFeet}'${lengthInch > 0 ? `${lengthInch}"` : ''}`;
    }

    for (const key in props) {
      if (['shape', 'color'].includes(key)) {
        ret[this.capitalize(key)] = this.capitalize(props[key]);
      }

      if (['finishing', 'rug pad'].includes(key)) {
        const id = +props[key];
        const products = [flowData.serging_product, flowData.rug_pad_product];
        const product = products.find(p => p.id === id || (p.variants || []).some(v => v.id === id));
        if (product) {
          const variant = (product.variants || []).find(v => v.id === id);
          if (variant) {
            ret[this.capitalize(key)] = variant.title;
          }
        }
      }
    }

    return ret;
  }

  capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
  }

  onSwatchAddCallback(id, requestedQuantity = 1, isLimitedSwatch = false) {
    const items = this.cartManager.getState()?.items || [];

    if (items.some(item => item.id === id)) {
      notifyDuplicateSwatch();
      return false;
    }

    if (isLimitedSwatch && requestedQuantity > 1) {
      notifyDuplicateSwatch();
      return false;
    }

    if (isLimitedSwatch && countLimitedSwatchUnits(items) + requestedQuantity > SWATCH_LIMIT) {
      notifySwatchLimit();
      return false;
    }

    return true;
  }

}

// Initialize all add-to-cart buttons on page load
function initializeAddToCartHandlers() {
  const buttons = document.querySelectorAll('button[type="submit"][add-to-cart], button[type="button"][add-to-cart]');
  buttons.forEach(button => {
    // Don't re-initialize if already handled
    if (button.dataset.addToCartInitialized) return;
    button.dataset.addToCartInitialized = 'true';
    new AddToCartHandler(button);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAddToCartHandlers);
} else {
  initializeAddToCartHandlers();
}

// Re-initialize on section load (for Shopify Sections API)
document.addEventListener('shopify:section:load', initializeAddToCartHandlers);

